import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, Settings, Sun, Moon, Monitor, Trash2, Key, AlertTriangle, Plus } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import { createPasskey, isWebAuthnSupported } from '../lib/webauthn'

const REFRESH_OPTIONS = [
  { value: '30', label: '30 seconds' },
  { value: '60', label: '1 minute' },
  { value: '120', label: '2 minutes' },
  { value: '300', label: '5 minutes' },
]

const THEME_OPTIONS = [
  { value: 'dark', label: 'Dark', icon: Moon, desc: 'Default dark interface' },
  { value: 'light', label: 'Light', icon: Sun, desc: 'Light background' },
  { value: 'crt', label: 'CRT', icon: Monitor, desc: 'Retro green phosphor overlay' },
]

function Toggle({ enabled, onChange, label }: { enabled: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className="flex items-center justify-between w-full py-2"
    >
      <span className="text-xs text-text-secondary">{label}</span>
      <div className={clsx(
        'w-9 h-5 rounded-full relative transition-colors',
        enabled ? 'bg-emerald' : 'bg-surface-2',
      )}>
        <div className={clsx(
          'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform',
          enabled ? 'translate-x-4' : 'translate-x-0.5',
        )} />
      </div>
    </button>
  )
}

interface PasskeyRow {
  id: string
  nickname: string
  transports: string[]
  aaguid: string
  sign_count: number
  created_at: string | null
  last_used_at: string | null
}

export default function SettingsPage() {
  const [loading, setLoading] = useState(true)
  const [feedInfo, setFeedInfo] = useState<any>(null)
  const [dbStats, setDbStats] = useState<any>(null)

  // ── WebAuthn / passkeys (Track 5.6) ────────────────────────────────────────
  const [passkeys, setPasskeys] = useState<PasskeyRow[]>([])
  const [passkeyStatus, setPasskeyStatus] = useState<{ library_available: boolean; library_version: string | null; note: string } | null>(null)
  const [passkeyError, setPasskeyError] = useState<string | null>(null)
  const [registering, setRegistering] = useState(false)
  const [newNickname, setNewNickname] = useState('')

  const refreshPasskeys = useCallback(async () => {
    try {
      const [list, status] = await Promise.all([
        api.get('/api/auth/webauthn/credentials'),
        api.get('/api/auth/webauthn/status'),
      ])
      setPasskeys(Array.isArray(list.data) ? list.data : [])
      setPasskeyStatus(status.data || null)
    } catch (e: any) {
      // 401 will be handled by the global interceptor; everything else is shown inline.
      if (e?.response?.status !== 401) {
        setPasskeyError(e?.response?.data?.detail || 'Failed to load passkeys')
      }
    }
  }, [])

  useEffect(() => { refreshPasskeys() }, [refreshPasskeys])

  const registerPasskey = async () => {
    setPasskeyError(null)
    if (!isWebAuthnSupported()) {
      setPasskeyError('WebAuthn requires a secure context (HTTPS or localhost). Open the dashboard via HTTPS to register a passkey.')
      return
    }
    setRegistering(true)
    try {
      const beginRes = await api.post('/api/auth/webauthn/register/begin', {})
      const attestation = await createPasskey(beginRes.data)
      await api.post('/api/auth/webauthn/register/finish', {
        credential: attestation,
        nickname: newNickname.trim() || null,
      })
      setNewNickname('')
      await refreshPasskeys()
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Registration failed'
      setPasskeyError(String(msg))
    } finally {
      setRegistering(false)
    }
  }

  const deletePasskey = async (id: string) => {
    setPasskeyError(null)
    try {
      await api.delete(`/api/auth/webauthn/credentials/${id}`)
      await refreshPasskeys()
    } catch (e: any) {
      setPasskeyError(e?.response?.data?.detail || 'Delete failed')
    }
  }

  // Profile
  const email = localStorage.getItem('cto-email') || 'unknown'
  const role = localStorage.getItem('cto-role') || 'viewer'

  // Notifications
  const [notifCVE, setNotifCVE] = useState(() => localStorage.getItem('cto-notif-cve') !== 'false')
  const [notifCloud, setNotifCloud] = useState(() => localStorage.getItem('cto-notif-cloud') !== 'false')
  const [notifBriefing, setNotifBriefing] = useState(() => localStorage.getItem('cto-notif-briefing') !== 'false')

  // Theme
  const [theme, setTheme] = useState(() => localStorage.getItem('cto-theme') || 'dark')

  // Refresh interval
  const [refreshInterval, setRefreshInterval] = useState(() => localStorage.getItem('cto-refresh-interval') || '60')

  // API keys
  const [nvdKey, setNvdKey] = useState(() => localStorage.getItem('cto-nvd-key') || '')
  const [ghToken, setGhToken] = useState(() => localStorage.getItem('cto-gh-token') || '')

  // Persist notification toggles
  useEffect(() => { localStorage.setItem('cto-notif-cve', String(notifCVE)) }, [notifCVE])
  useEffect(() => { localStorage.setItem('cto-notif-cloud', String(notifCloud)) }, [notifCloud])
  useEffect(() => { localStorage.setItem('cto-notif-briefing', String(notifBriefing)) }, [notifBriefing])
  useEffect(() => { localStorage.setItem('cto-theme', theme) }, [theme])
  useEffect(() => { localStorage.setItem('cto-refresh-interval', refreshInterval) }, [refreshInterval])

  const saveApiKeys = () => {
    localStorage.setItem('cto-nvd-key', nvdKey)
    localStorage.setItem('cto-gh-token', ghToken)
  }

  const fetchAboutData = useCallback(async () => {
    setLoading(true)
    try {
      const [feedsRes, dbRes] = await Promise.allSettled([
        api.get('/api/system/feeds'),
        api.get('/api/system/db-stats'),
      ])
      if (feedsRes.status === 'fulfilled') setFeedInfo(feedsRes.value.data)
      if (dbRes.status === 'fulfilled') setDbStats(dbRes.value.data)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAboutData() }, [fetchAboutData])

  return (
    <div className="space-y-4 animate-fade-in max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Settings size={20} className="text-blue" /> Settings
          </h1>
          <p className="text-[10px] text-text-secondary">Preferences and configuration</p>
        </div>
        <button type="button" onClick={fetchAboutData} className="btn-secondary text-xs">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Profile */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-text-primary mb-3">Profile</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] text-text-muted block mb-1">Email</label>
            <p className="text-xs text-text-secondary font-mono bg-surface-2 rounded px-3 py-2">{email}</p>
          </div>
          <div>
            <label className="text-[10px] text-text-muted block mb-1">Role</label>
            <p className="text-xs text-text-secondary font-mono bg-surface-2 rounded px-3 py-2 capitalize">{role}</p>
          </div>
        </div>
      </div>

      {/* Security — WebAuthn / FIDO2 passkeys */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <Key size={14} className="text-emerald" /> Security — Passkeys
          </h2>
          {passkeyStatus && !passkeyStatus.library_available && (
            <span className="text-[10px] text-amber bg-amber/10 border border-amber/20 rounded px-2 py-0.5">
              Library missing — rebuild pending
            </span>
          )}
        </div>

        <p className="text-[10px] text-text-muted mb-3">
          Phishing-resistant FIDO2 / WebAuthn passkeys. Sit alongside your TOTP code; banks moving
          to phishing-resistant MFA need this. TOTP keeps working until you explicitly disable it.
        </p>

        {passkeyStatus && !passkeyStatus.library_available && (
          <div className="text-[10px] text-amber bg-amber/10 border border-amber/20 rounded px-3 py-2 mb-3 flex items-start gap-2">
            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
            <span>
              {passkeyStatus.note}
            </span>
          </div>
        )}

        {passkeys.length === 0 ? (
          <p className="text-xs text-text-secondary italic mb-3">No passkeys registered yet.</p>
        ) : (
          <div className="space-y-2 mb-3">
            {passkeys.map(pk => (
              <div key={pk.id} className="flex items-center justify-between bg-surface-2 rounded px-3 py-2">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-text-primary truncate">
                    {pk.nickname || '(unnamed passkey)'}
                  </p>
                  <p className="text-[10px] text-text-muted">
                    {(pk.transports && pk.transports.length > 0) ? pk.transports.join(', ') : 'unknown transport'}
                    {pk.last_used_at ? ` · last used ${new Date(pk.last_used_at).toLocaleDateString()}` : ' · never used'}
                    {' · '}sign count {pk.sign_count}
                  </p>
                </div>
                <button
                  type="button"
                  title={`Delete passkey ${pk.nickname || pk.id}`}
                  onClick={() => deletePasskey(pk.id)}
                  className="btn-secondary text-[10px] flex-shrink-0"
                >
                  <Trash2 size={10} /> Remove
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label htmlFor="passkey-nickname" className="text-[10px] text-text-muted block mb-1">
              Nickname (optional, e.g. "YubiKey 5", "MacBook Touch ID")
            </label>
            <input
              id="passkey-nickname"
              type="text"
              value={newNickname}
              onChange={e => setNewNickname(e.target.value)}
              placeholder="My security key"
              maxLength={120}
              className="input py-2 text-xs"
            />
          </div>
          <button
            type="button"
            onClick={registerPasskey}
            disabled={registering || (passkeyStatus?.library_available === false)}
            className="btn-primary text-xs whitespace-nowrap"
          >
            <Plus size={12} /> {registering ? 'Registering…' : 'Register a new passkey'}
          </button>
        </div>

        {passkeyError && (
          <div className="flex items-start gap-2 mt-3 text-xs text-rose-light">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" /> <span>{passkeyError}</span>
          </div>
        )}
      </div>

      {/* Notifications */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-text-primary mb-3">Discord Notifications</h2>
        <p className="text-[10px] text-text-muted mb-3">Control which alerts are sent to Discord</p>
        <div className="divide-y divide-border">
          <Toggle label="CVE / Vulnerability Alerts" enabled={notifCVE} onChange={setNotifCVE} />
          <Toggle label="Cloud Outage Alerts" enabled={notifCloud} onChange={setNotifCloud} />
          <Toggle label="Daily Briefing Summary" enabled={notifBriefing} onChange={setNotifBriefing} />
        </div>
      </div>

      {/* Theme */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-text-primary mb-3">Theme</h2>
        <div className="grid grid-cols-3 gap-3">
          {THEME_OPTIONS.map(opt => {
            const Icon = opt.icon
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setTheme(opt.value)}
                className={clsx(
                  'card-hover p-4 rounded-lg border text-center transition-all',
                  theme === opt.value
                    ? 'border-blue bg-blue/10 ring-1 ring-blue/30'
                    : 'border-border',
                )}
              >
                <Icon size={20} className={clsx('mx-auto mb-2', theme === opt.value ? 'text-blue' : 'text-text-muted')} />
                <p className="text-xs font-semibold text-text-primary">{opt.label}</p>
                <p className="text-[10px] text-text-muted mt-1">{opt.desc}</p>
              </button>
            )
          })}
        </div>
        {theme === 'crt' && (
          <div className="mt-3 text-[10px] text-emerald bg-emerald/10 border border-emerald/20 rounded px-3 py-2">
            CRT mode applies a green phosphor overlay for a retro terminal look.
          </div>
        )}
      </div>

      {/* Data Refresh */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-text-primary mb-3">Data Refresh Interval</h2>
        <p className="text-[10px] text-text-muted mb-3">How often dashboards auto-refresh data</p>
        <select
          title="Refresh interval"
          value={refreshInterval}
          onChange={e => setRefreshInterval(e.target.value)}
          className="input py-2 text-xs w-48"
        >
          {REFRESH_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* API Keys */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-text-primary mb-3">API Keys</h2>
        <p className="text-[10px] text-text-muted mb-3">Optional keys stored locally in your browser. Not sent to any server.</p>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-text-muted block mb-1">NVD API Key</label>
            <input
              type="password"
              value={nvdKey}
              onChange={e => setNvdKey(e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="input py-2 text-xs"
            />
          </div>
          <div>
            <label className="text-[10px] text-text-muted block mb-1">GitHub Token</label>
            <input
              type="password"
              value={ghToken}
              onChange={e => setGhToken(e.target.value)}
              placeholder="ghp_..."
              className="input py-2 text-xs"
            />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={saveApiKeys} className="btn-primary text-xs">
              Save Keys
            </button>
            <button
              type="button"
              onClick={() => { setNvdKey(''); setGhToken(''); localStorage.removeItem('cto-nvd-key'); localStorage.removeItem('cto-gh-token') }}
              className="btn-secondary text-xs"
            >
              <Trash2 size={12} /> Clear All
            </button>
          </div>
        </div>
      </div>

      {/* About */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-text-primary mb-3">About</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="stat-card">
            <p className="text-[10px] text-text-muted">Version</p>
            <p className="text-sm font-bold text-text-primary">3.0</p>
          </div>
          <div className="stat-card">
            <p className="text-[10px] text-text-muted">Feeds</p>
            <p className="text-sm font-bold text-text-primary">{feedInfo?.total_feeds ?? '...'}</p>
          </div>
          <div className="stat-card">
            <p className="text-[10px] text-text-muted">Total Articles</p>
            <p className="text-sm font-bold text-text-primary">{dbStats?.total_articles?.toLocaleString() ?? '...'}</p>
          </div>
          <div className="stat-card">
            <p className="text-[10px] text-text-muted">Entities</p>
            <p className="text-sm font-bold text-text-primary">{dbStats?.total_entities?.toLocaleString() ?? '...'}</p>
          </div>
          <div className="stat-card">
            <p className="text-[10px] text-text-muted">CVEs Tracked</p>
            <p className="text-sm font-bold text-text-primary">{dbStats?.total_cves?.toLocaleString() ?? '...'}</p>
          </div>
          <div className="stat-card">
            <p className="text-[10px] text-text-muted">DB Size</p>
            <p className="text-sm font-bold text-text-primary">{dbStats?.db_size_mb ? `${dbStats.db_size_mb} MB` : '...'}</p>
          </div>
        </div>
        {feedInfo?.categories && (
          <div className="mt-3">
            <p className="text-[10px] text-text-muted mb-2">Data Sources</p>
            <div className="flex flex-wrap gap-1">
              {Object.entries(feedInfo.categories).map(([cat, count]: [string, any]) => (
                <span key={cat} className="badge-blue text-[10px]">{cat}: {Array.isArray(count) ? count.length : count}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
